import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { ChangeEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { Button } from '@ui/components/Button'
import { FileData } from '@ui/components/FileUpload/types'
import { PageSection } from '@ui/components/PageSection'

import { validateManualAddressFields } from '@utils/address.utils'
import { isTickmillSC, isTickmillUAE } from '@utils/companyName.utils'

import { AccountDetailedDto, isAccountDormantUpdateNeeded } from '@model/AccountDetailedDto'
import { ClientTestDto } from '@model/ClientTestDto'
import { CountryPreferenceStrippedDto } from '@model/CountryDto'
import { NameDto } from '@model/NameDto'
import { TestSectionDto } from '@model/TestSectionsDto'

import { useClientPhoneNumberValidation } from '@hooks/useClientPhoneNumberValidation'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { getCleanedAnswers, validateTestForm } from '@shared/templates/Test/TestFields/TestFields'

import { YearlyKycPersonalInfoForm } from './YearlyKycPersonalInfoForm'
import { YearlyKycTestForm } from './YearlyKycTestForm'

export interface YearlyKycUpdateDetailsFormValues {
  clientId: string
  emailAddress: string
  countryCode: string
  countryName: string
  phoneNumber: string
  state: string
  city: string
  street: string
  streetHouseNo: string
  streetHouseNoNotAvailable?: boolean
  zipCode: string
  taxIdentificationNumber: string
  kycUpdateTestId: string
  taxIdAvailable: boolean

  selectedAnswers: { [key: string]: string | string[] }
  freeAnswers: { [key: string]: string }

  documents: FileData[]
  accountKycTest: ClientTestDto | undefined
  countries: CountryPreferenceStrippedDto[]
  taxIdUnavailableReasonId?: number
}

export const YearlyKycUpdateDetailsFormUI: React.FC<
  FormikProps<YearlyKycUpdateDetailsFormValues> & OuterProps
> = (props) => {
  const {
    values,
    account,
    handleSubmit,
    isSubmitting,
    initialValues,
    setFieldError,
    errors,
    tinUnavailableReasons,
    submitCount,
    isValid,
  } = props

  const { t } = useTranslation()

  const isPhoneNumberChanged =
    values.countryCode !== initialValues.countryCode ||
    values.phoneNumber !== initialValues.phoneNumber

  const hasPhoneNumberFormError =
    !values.countryCode || !values.phoneNumber || !!errors.countryCode || !!errors.phoneNumber

  const shouldValidatePhoneNumber = isPhoneNumberChanged && !hasPhoneNumberFormError

  const { isValid: isPhoneNumberValid, isInvalid: isPhoneNumberInvalid } =
    useClientPhoneNumberValidation({
      countryCode: values.countryCode,
      phoneNumber: values.phoneNumber,
      enabled: shouldValidatePhoneNumber,
    })

  const phoneNumberError = isPhoneNumberInvalid
    ? t('Sign up.Please check your phone number')
    : errors.phoneNumber

  const isPhoneNumberReady =
    !hasPhoneNumberFormError && (!isPhoneNumberChanged || isPhoneNumberValid)

  const proofOfAddressNeeded = () =>
    initialValues.street !== values.street ||
    initialValues.city !== values.city ||
    initialValues.zipCode !== values.zipCode ||
    initialValues.state !== values.state ||
    initialValues.streetHouseNo !== values.streetHouseNo

  const isProofOfAddressNeeded = proofOfAddressNeeded()

  const isDocumentUploaded =
    !isProofOfAddressNeeded || values.documents.some((doc) => doc?.fileName && !doc.error)

  const isDormantOnly =
    isAccountDormantUpdateNeeded(account) &&
    !account?.isKYCUpdateNeeded &&
    !props.productRegistrationRequirement

  const isAnyFieldChanged =
    initialValues.street !== values.street ||
    initialValues.city !== values.city ||
    initialValues.zipCode !== values.zipCode ||
    initialValues.state !== values.state ||
    initialValues.countryCode !== values.countryCode ||
    initialValues.phoneNumber !== values.phoneNumber

  const isSubmitEnabled =
    isPhoneNumberReady &&
    (isDormantOnly ? submitCount === 0 || isAnyFieldChanged : isValid && isDocumentUploaded)

  useEffect(() => {
    if (
      account?.isKYCUpdateNeeded &&
      isProofOfAddressNeeded &&
      !('documents' in errors) &&
      !values.documents.some((doc) => doc?.fileName && !doc.error)
    ) {
      setFieldError('documents', '')
    }
  }, [account?.isKYCUpdateNeeded, isProofOfAddressNeeded, errors, values.documents, setFieldError])

  const handleSubmitForm = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleSubmit()
  }

  return (
    <Loading isLoading={isSubmitting} showLoadingIcon text={t('Wallet.Submitting')}>
      <Form onSubmit={handleSubmitForm}>
        {!props.productRegistrationRequirement && (
          <PageSection title={t('Profile.Are your details up to date?')} />
        )}

        <Box flex direction={{ desktop: 'row', mobile: 'column' }} gap='x16' mt='x16' fullWidth>
          {account && (
            <KycUpdateDetailsForm
              account={account}
              proofOfAddressNeeded={proofOfAddressNeeded}
              phoneNumberError={phoneNumberError}
              tinUnavailableReasons={tinUnavailableReasons}
              productRegistrationRequirement={props.productRegistrationRequirement}
            />
          )}
        </Box>

        <Box flex justify='center' mt='x24' fullWidth>
          <Box fullWidth={{ mobile: true }} minWidth={{ desktop: 190 }}>
            <Button.Primary size='lg' type='submit' disabled={!isSubmitEnabled} fullWidth>
              {t('Submit')}
            </Button.Primary>
          </Box>
        </Box>
      </Form>
    </Loading>
  )
}

interface KycUpdateDetailsFormProps {
  account: AccountDetailedDto | undefined
  proofOfAddressNeeded: () => boolean
  phoneNumberError?: string
  productRegistrationRequirement?: boolean
  tinUnavailableReasons: NameDto[]
}

const KycUpdateDetailsForm: React.FC<KycUpdateDetailsFormProps> = (props) => {
  const { account, proofOfAddressNeeded, phoneNumberError, tinUnavailableReasons } = props
  const { isDormantUpdateNeeded, isKYCUpdateNeeded } = account || {}

  if (isKYCUpdateNeeded || props.productRegistrationRequirement) {
    return (
      <>
        <Box flexItem='1 1 0' fullWidth>
          <YearlyKycPersonalInfoForm
            account={account}
            proofOfAddressNeeded={proofOfAddressNeeded}
            phoneNumberError={phoneNumberError}
            tinUnavailableReasons={tinUnavailableReasons}
            productRegistrationRequirement={props.productRegistrationRequirement}
          />
        </Box>

        <Box flexItem='1 1 0' fullWidth>
          <YearlyKycTestForm />
        </Box>
      </>
    )
  }

  if (isDormantUpdateNeeded) {
    return (
      <Box flexItem='1 1 0' fullWidth>
        <YearlyKycPersonalInfoForm
          account={account}
          proofOfAddressNeeded={proofOfAddressNeeded}
          phoneNumberError={phoneNumberError}
          tinUnavailableReasons={tinUnavailableReasons}
          productRegistrationRequirement={props.productRegistrationRequirement}
        />
      </Box>
    )
  }

  return null
}

interface OuterProps {
  account: AccountDetailedDto | undefined
  countries: CountryPreferenceStrippedDto[]
  accountKycTest: ClientTestDto | undefined
  onSubmit(values: YearlyKycUpdateDetailsFormValues): void
  tinUnavailableReasons: NameDto[]
  productRegistrationRequirement?: boolean
}

const initialState = ({ account }: { account: AccountDetailedDto | undefined }) => {
  const phoneNumbers = account?.phoneNumbers ?? []
  const primaryPhoneNumber = phoneNumbers.find((phoneNumber) => phoneNumber.isPrimary)
  const [address] = account?.addresses || []
  const [email] = account?.emails || []

  return {
    clientId: account?.id || '',
    emailAddress: email?.address || '',
    countryCode: primaryPhoneNumber?.countryCode || '',
    countryName: address?.country?.name || '',
    phoneNumber: primaryPhoneNumber?.number || '',
    state: address?.state || '',
    streetHouseNo: address?.streetHouseNo ?? '',
    streetHouseNoNotAvailable: !isTickmillUAE(account) && !address?.streetHouseNo?.trim(),
    city: address?.city || '',
    street: address?.street || '',
    zipCode: address?.postalCode || '',
    taxIdentificationNumber: account?.taxId || '',
    kycUpdateTestId: '',
    taxIdUnavailableReasonId: account?.taxIdAvailabilityStatus?.id,
  }
}

const dataToSubmit = (
  values: YearlyKycUpdateDetailsFormValues,
  sections: readonly TestSectionDto[]
): YearlyKycUpdateDetailsFormValues => {
  const { selectedAnswers: cleanSelectedAnswers, freeAnswers: cleanFreeAnswers } =
    getCleanedAnswers(values.selectedAnswers, values.freeAnswers, sections)

  return {
    ...values,
    selectedAnswers: cleanSelectedAnswers,
    freeAnswers: cleanFreeAnswers,
  }
}

export const YearlyKycUpdateDetailsForm = withFormik<OuterProps, YearlyKycUpdateDetailsFormValues>({
  mapPropsToValues: ({ account, countries, accountKycTest }) => ({
    ...initialState({ account }),
    accountKycTest: account?.isKYCUpdateNeeded ? accountKycTest : undefined,
    selectedAnswers: {},
    freeAnswers: {},
    documents: [],
    countries: countries || [],
    taxIdAvailable: !!account?.taxId,
  }),
  mapPropsToTouched: () => ({
    street: true,
    city: true,
    zipCode: true,
    state: true,
  }),
  handleSubmit: (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      props.onSubmit(dataToSubmit(values, props.accountKycTest?.sections || []))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    let errors: FormikErrors<YearlyKycUpdateDetailsFormValues> = {}

    const isDormantOnly =
      isAccountDormantUpdateNeeded(props.account) &&
      !props.account?.isKYCUpdateNeeded &&
      !props.productRegistrationRequirement

    if (values.accountKycTest?.sections && !isDormantOnly) {
      const testErrors = validateTestForm(values.accountKycTest.sections, values as any, t)

      errors = { ...errors, ...testErrors }
    }

    if (!values?.countryCode) {
      errors.countryCode = t('Validation.Required')
    }

    if (!values?.phoneNumber) {
      errors.phoneNumber = t('Validation.Required')
    }

    Object.assign(
      errors,
      validateManualAddressFields(
        values,
        {
          address: 'street',
          city: 'city',
          state: 'state',
          postalCode: 'zipCode',
          streetHouseNumber: 'streetHouseNo',
        },
        {
          streetHouseNumber: {
            streetHouseNumberNotAvailable: values.streetHouseNoNotAvailable,
          },
        }
      )
    )

    if (!isTickmillSC(props.account)) {
      if (!values?.taxIdentificationNumber && values.taxIdAvailable) {
        errors.taxIdentificationNumber = t('Validation.Required')
      }

      if (!values?.taxIdUnavailableReasonId && !values.taxIdAvailable) {
        errors.taxIdUnavailableReasonId = t('Validation.Required')
      }
    }

    return errors
  },

  enableReinitialize: true,
  validateOnMount: true,
})(YearlyKycUpdateDetailsFormUI)
